import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';
import { EASE, lift } from './motion';
import { BRAND, MINT_GLOW, SHADOW_LG, colourCycle } from './tokens';

/** The row of stat cards under the banner: two columns on phones, up to six from tablet width. */
export function StatGrid({ cols = 4, children }: { cols?: number; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: `repeat(${String(Math.min(cols, 6))}, minmax(0, 1fr))`,
        },
        ...colourCycle(),
      }}
    >
      {children}
    </Box>
  );
}

/**
 * One figure: a label, its value and an icon on a badge. Inside a `StatGrid` each card takes the
 * next colour of the cycle (a white icon on its gradient); on its own the badge is mint. With `to`,
 * the whole card is the link to the records behind the figure.
 */
export function StatCard({
  label,
  value,
  icon,
  to,
}: {
  label: string;
  value: ReactNode;
  icon: ReactNode;
  to?: string;
}) {
  return (
    <Paper
      {...(to === undefined ? {} : { component: RouterLink, to })}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: 90,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        boxShadow: SHADOW_LG,
        color: 'text.primary',
        textDecoration: 'none',
        ...lift,
        '&:hover .stat-icon': { transform: 'scale(1.1) rotate(-4deg)', bgcolor: BRAND.mint },
        // A soft glow of the card's colour in its corner.
        '&::before': {
          content: '""',
          position: 'absolute',
          right: -28,
          top: -28,
          width: 96,
          height: 96,
          borderRadius: '50%',
          bgcolor: `var(--cycle-light, ${BRAND.mint})`,
          opacity: 0.14,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14, lineHeight: '20px', color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography
          component="div"
          noWrap
          sx={{
            fontSize: 24,
            lineHeight: '32px',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        aria-hidden
        className="stat-icon"
        sx={{
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          width: 48,
          height: 48,
          borderRadius: '14px',
          position: 'relative',
          // In a StatGrid: a white icon on the card's colour; otherwise charcoal on mint.
          color: `var(--cycle-icon, ${BRAND.onMint})`,
          bgcolor: MINT_GLOW,
          backgroundImage:
            'linear-gradient(135deg, var(--cycle-light, transparent), var(--cycle-dark, transparent))',
          boxShadow: '0 10px 18px -10px var(--cycle-dark, transparent)',
          transition: `transform 220ms ${EASE}, background-color 220ms ${EASE}`,
          '& svg': { fontSize: 26 },
        }}
      >
        {icon}
      </Box>
    </Paper>
  );
}
